import { useEffect, useState, useCallback } from "react";
import { supabase } from "./supabase";
import { readCache, writeCache, cacheFresh } from "./useCachedQuery";

/**
 * Loads the profile row for the given user id. Returns:
 *   { profile, loading, error, refresh, upsert }
 *
 *   - profile: null if no row exists yet (user hasn't completed onboarding)
 *   - upsert(partial): insert-or-update the profile row for this user
 */
// Profile is one row, fetched on every app boot. Slow-changing
// (name / dietary / avatar updates happen rarely). 6h TTL — short
// enough that a profile edit on another device propagates within
// the day; long enough to skip the boot fetch on most sessions.
// upsert() below also updates the cache directly so the user's
// own edits surface instantly.
// v2: migration 0281 added onboarded_at, which the onboarding gate
// keys on. A v1 cache row predates the column — cacheFresh() would
// skip the fetch and the missing key would dump an onboarded user
// back into onboarding. The key bump forces one fresh fetch.
const CACHE_KEY = (uid) => `profile.v2:${uid}`;
const TTL_MS    = 6 * 60 * 60 * 1000;

export function useProfile(userId) {
  const cached = userId ? readCache(CACHE_KEY(userId), TTL_MS) : null;
  const [profile, setProfile] = useState(() => cached ?? null);
  const [loading, setLoading] = useState(() => userId ? cached === null : false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!userId) {
      setProfile(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    const { data, error } = await supabase
      .from("profiles")
      .select("*")
      .eq("id", userId)
      .maybeSingle();
    if (error) setError(error);
    setProfile(data ?? null);
    if (data) writeCache(CACHE_KEY(userId), data);
    setLoading(false);
  }, [userId]);

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }
    if (cacheFresh(CACHE_KEY(userId), TTL_MS)) {
      setLoading(false);
      return;
    }
    load();
  }, [userId, load]);

  const upsert = useCallback(
    async (partial) => {
      if (!userId) throw new Error("upsert called without a userId");
      const row = { id: userId, ...partial };
      const { data, error } = await supabase
        .from("profiles")
        .upsert(row, { onConflict: "id" })
        .select()
        .single();
      if (error) throw error;
      setProfile(data);
      // Persist the fresh shape — without this the next boot would
      // re-fetch from the network because the localStorage copy is
      // stale relative to what we just wrote.
      writeCache(CACHE_KEY(userId), data);
      return data;
    },
    [userId]
  );

  // Local-only patch for fields written by server-side RPCs (e.g.
  // grant_starter_avatars / set_avatar bump avatar_slug + avatar_url).
  // The RPC already wrote to DB — this just syncs the in-memory copy
  // so the UI re-renders without a round-trip SELECT.
  const patchLocal = useCallback((partial) => {
    setProfile((p) => (p ? { ...p, ...partial } : p));
  }, []);

  return { profile, loading, error, refresh: load, upsert, patchLocal };
}
